import { version as appVersion } from '../../package.json';
import { createDiagnosticMasker, redactSecrets } from './diagnosticPrivacy';
import { getRuntimeInfo } from './runtimeInfo';
import {
  sanitizeBlueprintValidation,
  type BlueprintValidationDiagnostic,
} from '@/common/utils/blueprintValidationDiagnostics';

export interface DiagnosticFile {
  fileName: string;
  size?: number;
  fullHash?: string;
  quickHash?: string;
  fileId?: string;
  _id?: string;
  replacesFileId?: string;
  status?: string;
}
export interface TransferDiagnosticContext {
  projectId?: string;
  projectName?: string;
  blueprintId?: string;
  blueprintName?: string;
  version?: number;
  versionId?: string;
  jobId?: string;
  destinationKind?: string;
  files?: DiagnosticFile[];
  deletedFileIds?: string[];
}
export interface TransferErrorReport {
  reportId: string;
  recordedAt: string;
  appVersion: string;
  runtime: string;
  environment: {
    userAgent: string;
    online: boolean;
    buildMode: string;
    nativeApiAvailable?: boolean;
    nativeFileApiAvailable?: boolean;
  };
  operation: string;
  context: TransferDiagnosticContext;
  blueprintValidation?: BlueprintValidationDiagnostic[];
  errors: {
    at: string;
    stage: string;
    message: string;
    file?: DiagnosticFile;
  }[];
}

// Reports deliberately exclude arbitrary exception objects, headers, mutation
// variables, stacks, storage keys and source content. Identifying names/paths
// stay in the in-memory collector only; formatTransferReport is the sharing boundary.
export function redactDiagnosticText(value: string): string {
  return value
    .replace(/(?:https?|file):\/\/[^\s<>"']+/gi, '[URL redacted]')
    .replace(/\bBearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+\b/g, '[token redacted]')
    .replace(/\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g, '[key redacted]')
    .replace(
      /\b(?:token|accessToken|refreshToken|password|secret|authorization|cookie|x-amz-signature)["']?\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi,
      '[credential redacted]'
    )
    .replace(/(?:[A-Za-z]:[\\/]|\\\\)[^\r\n"'<>]+/g, '[local path redacted]')
    .replace(/(^|[\s("'=])\/(?!\s)[^\r\n"'<>]*/g, '$1[local path redacted]');
}

function safeFile(file: DiagnosticFile): DiagnosticFile {
  return {
    fileName: redactSecrets(file.fileName),
    size: file.size,
    fullHash: file.fullHash,
    quickHash: file.quickHash,
    fileId: file.fileId ?? file._id,
    replacesFileId: file.replacesFileId,
    status: file.status,
  };
}
function errorMessages(error: unknown, seen = new Set<unknown>()): string[] {
  if (seen.has(error)) return [];
  seen.add(error);
  if (!error || typeof error !== 'object')
    return [String(error ?? 'Unknown error')];
  const item = error as {
    name?: string;
    message?: string;
    code?: string;
    statusCode?: number;
    cause?: unknown;
    graphQLErrors?: unknown[];
    networkError?: unknown;
    errors?: unknown[];
  };
  const messages = [String(item.message ?? 'Unknown error')];
  if (item.code) messages.push(`Code: ${item.code}`);
  if (item.statusCode) messages.push(`HTTP status: ${item.statusCode}`);
  for (const child of [
    ...(Array.isArray(item.graphQLErrors) ? item.graphQLErrors : []),
    ...(Array.isArray(item.errors) ? item.errors : []),
    item.cause,
    item.networkError,
  ]) {
    if (child && seen.size < 100) messages.push(...errorMessages(child, seen));
    else if (child && !seen.has(child))
      messages.push(
        'Additional nested error details omitted at the safety limit.'
      );
  }
  return messages;
}

function blueprintValidationDetails(
  error: unknown
): BlueprintValidationDiagnostic[] {
  const pending = [error];
  const seen = new Set<unknown>();
  const found: BlueprintValidationDiagnostic[] = [];
  while (pending.length && seen.size < 100 && found.length < 10) {
    const item = pending.shift();
    if (!item || typeof item !== 'object' || seen.has(item)) continue;
    seen.add(item);
    const value = item as Record<string, unknown>;
    const extensions = value.extensions as
      | {
          blueprintValidation?: unknown;
          originalError?: { blueprintValidation?: unknown };
        }
      | undefined;
    const safe = sanitizeBlueprintValidation(
      extensions?.blueprintValidation ??
        extensions?.originalError?.blueprintValidation
    );
    if (safe && !found.some((v) => JSON.stringify(v) === JSON.stringify(safe)))
      found.push(safe);
    for (const children of [
      value.graphQLErrors,
      value.errors,
      (value.result as { errors?: unknown } | undefined)?.errors,
    ]) {
      if (Array.isArray(children))
        pending.push(...children.slice(0, 100 - seen.size));
    }
    pending.push(value.cause, value.networkError);
  }
  return found;
}

let reports: TransferErrorReport[] = [];
const listeners = new Set<() => void>();
const reportedErrors = new WeakSet<object>();
const recentScans = new Map<string, number>();
export const getTransferReports = () => reports;
export const subscribeTransferReports = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export function dismissTransferReport(reportId: string) {
  reports = reports.filter((report) => report.reportId !== reportId);
  listeners.forEach((listener) => listener());
}

export function createTransferDiagnostics(
  operation: string,
  initial: TransferDiagnosticContext = {}
) {
  let context = initial;
  const errors: TransferErrorReport['errors'] = [];
  const originals: object[] = [];
  const blueprintValidation: BlueprintValidationDiagnostic[] = [];
  let published = false;
  return {
    update(next: Partial<TransferDiagnosticContext>) {
      context = { ...context, ...next };
    },
    add(stage: string, error: unknown, file?: DiagnosticFile) {
      if (error && typeof error === 'object') {
        if (reportedErrors.has(error)) return;
        originals.push(error);
      }
      for (const detail of blueprintValidationDetails(error)) {
        if (
          blueprintValidation.length < 10 &&
          !blueprintValidation.some(
            (v) => JSON.stringify(v) === JSON.stringify(detail)
          )
        )
          blueprintValidation.push(detail);
      }
      for (const message of errorMessages(error)) {
        const safeMessage = redactSecrets(message);
        if (
          !errors.some(
            (entry) =>
              entry.stage === stage &&
              entry.message === safeMessage &&
              entry.file?.fileName === file?.fileName
          )
        )
          errors.push({
            at: new Date().toISOString(),
            stage,
            message: safeMessage,
            file: file ? safeFile(file) : undefined,
          });
      }
    },
    publish() {
      if (published || !errors.length) return;
      published = true;
      originals.forEach((error) => reportedErrors.add(error));
      // Passive scans must not interrupt the user with the same modal every minute.
      if (operation === 'Background scan') {
        const key = `${context.projectId}:${context.version}:${errors.map((error) => `${error.stage}:${error.file?.fileName}:${error.message}`).join('|')}`;
        if (Date.now() - (recentScans.get(key) ?? 0) < 5 * 60_000) return;
        if (recentScans.size > 100) recentScans.clear();
        recentScans.set(key, Date.now());
      }
      const safeContext: TransferDiagnosticContext = {
        projectId: context.projectId,
        projectName: context.projectName,
        blueprintId: context.blueprintId,
        blueprintName: context.blueprintName,
        version: context.version,
        versionId: context.versionId,
        jobId: context.jobId,
        destinationKind: context.destinationKind,
        deletedFileIds: context.deletedFileIds,
        files: context.files?.map(safeFile),
      };
      const runtimeInfo = getRuntimeInfo();
      const report = {
        reportId: crypto.randomUUID(),
        recordedAt: new Date().toISOString(),
        appVersion,
        runtime: runtimeInfo.runtime,
        environment: {
          userAgent:
            typeof navigator === 'undefined' ? 'Unknown' : navigator.userAgent,
          online: typeof navigator !== 'undefined' && navigator.onLine,
          buildMode: import.meta.env.MODE,
          nativeApiAvailable: runtimeInfo.nativeApiAvailable,
          nativeFileApiAvailable: runtimeInfo.nativeFileApiAvailable,
        },
        operation,
        context: safeContext,
        blueprintValidation: blueprintValidation.length
          ? blueprintValidation
          : undefined,
        errors,
      };
      // Sanitize the complete allowlisted payload too, including user-supplied names.
      reports = [
        ...reports,
        JSON.parse(
          JSON.stringify(report, (_key, value) =>
            typeof value === 'string' ? redactSecrets(value) : value
          )
        ) as TransferErrorReport,
      ];
      listeners.forEach((listener) => listener());
    },
  };
}

export function reportTransferError(
  operation: string,
  error: unknown,
  context: TransferDiagnosticContext = {}
) {
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    ['AbortError', 'UploadAbortError'].includes(String(error.name))
  )
    return;
  const diagnostic = createTransferDiagnostics(operation, context);
  diagnostic.add(operation, error);
  diagnostic.publish();
}
const formattedReports = new WeakMap<TransferErrorReport, string>();
export function formatTransferReport(report: TransferErrorReport) {
  const existing = formattedReports.get(report);
  if (existing) return existing;
  const mask = createDiagnosticMasker();
  mask.remember(report.context.projectName);
  mask.remember(report.context.blueprintName);
  for (const file of [
    ...(report.context.files ?? []),
    ...report.errors.flatMap((error) => (error.file ? [error.file] : [])),
  ]) {
    mask.remember(file.fileName, true);
    // Error messages sometimes use the basename rather than the relative path.
    mask.remember(file.fileName.split(/[\\/]/).pop(), true);
  }
  const file = (value: DiagnosticFile) => ({
    ...safeFile(value),
    fileName: mask.path(value.fileName, true),
  });
  const shared = {
    reportId: report.reportId,
    recordedAt: report.recordedAt,
    appVersion: report.appVersion,
    runtime: report.runtime,
    environment: {
      userAgent: report.environment.userAgent,
      online: report.environment.online,
      buildMode: report.environment.buildMode,
      nativeApiAvailable: report.environment.nativeApiAvailable,
      nativeFileApiAvailable: report.environment.nativeFileApiAvailable,
    },
    privacy:
      'Names and paths masked per report. Spacing, punctuation, common folders and known extensions preserved. IDs, hashes and sizes retained for debugging. Validation details contain only allowlisted technical facts; raw bytes, archive entry names, input values and credentials are omitted.',
    operation: mask.text(report.operation),
    context: {
      projectId: report.context.projectId,
      projectName: report.context.projectName
        ? mask.name(report.context.projectName)
        : undefined,
      blueprintId: report.context.blueprintId,
      blueprintName: report.context.blueprintName
        ? mask.name(report.context.blueprintName)
        : undefined,
      version: report.context.version,
      versionId: report.context.versionId,
      jobId: report.context.jobId,
      destinationKind: report.context.destinationKind,
      deletedFileIds: report.context.deletedFileIds,
      files: report.context.files?.map(file),
    },
    errors: report.errors.map((error) => ({
      at: error.at,
      stage: mask.text(error.stage),
      message: mask.text(error.message),
      file: error.file ? file(error.file) : undefined,
    })),
    blueprintValidation: report.blueprintValidation
      ?.slice(0, 10)
      .map(sanitizeBlueprintValidation)
      .filter(Boolean),
  };
  const text = `Studio Vault beta — transfer diagnostic report\n${JSON.stringify(shared, (_key, value) => (typeof value === 'string' ? redactSecrets(value) : value), 2)}`;
  formattedReports.set(report, text);
  return text;
}
